import { Badge } from '@ophelos/ui/components/badge';
import { Button } from '@ophelos/ui/components/button';
import { Card, CardContent, CardHeader, CardTitle } from '@ophelos/ui/components/card';
import { ChevronLeft, ChevronRight, Loader2, Sparkles } from 'lucide-react';
import { useState } from 'react';

export interface MatchSuggestionCandidate {
  case_id: string;
  confidence: number;
  confidence_summary: string;
  signals?: Array<{ label: string; score: number }>;
}

interface MatchSuggestionCardProps {
  bankLineId: string;
  candidates: MatchSuggestionCandidate[];
  onMatch: (caseId: string) => void;
  onDismiss: () => void;
  isMatching?: boolean;
}

function confidenceVariant(confidence: number) {
  if (confidence >= 80) return 'success' as const;
  if (confidence >= 50) return 'warning' as const;
  return 'subtle' as const;
}

export function MatchSuggestionCard({
  bankLineId: _bankLineId,
  candidates,
  onMatch,
  onDismiss,
  isMatching = false,
}: MatchSuggestionCardProps) {
  const [currentIndex, setCurrentIndex] = useState(0);
  const current = candidates[currentIndex];
  const hasMultiple = candidates.length > 1;

  const handlePrev = () => setCurrentIndex((i) => (i <= 0 ? candidates.length - 1 : i - 1));
  const handleNext = () => setCurrentIndex((i) => (i >= candidates.length - 1 ? 0 : i + 1));

  return (
    <Card aria-label="Match suggestion" className="py-3 bg-soft">
      <CardHeader className="flex items-center justify-between gap-2 px-3">
        <div className="flex items-center gap-2">
          <Sparkles className="size-3.5 text-brand-600 dark:text-brand-400 shrink-0" aria-hidden />
          <CardTitle>Suggested match</CardTitle>
        </div>
        {hasMultiple && (
          <div className="flex items-center gap-0.5">
            <Button variant="ghost" size="icon-sm" onClick={handlePrev} disabled={isMatching} aria-label="Previous candidate">
              <ChevronLeft className="size-4" />
            </Button>
            <span className="text-xs text-muted-foreground tabular-nums px-1" aria-live="polite">
              {currentIndex + 1}/{candidates.length}
            </span>
            <Button variant="ghost" size="icon-sm" onClick={handleNext} disabled={isMatching} aria-label="Next candidate">
              <ChevronRight className="size-4" />
            </Button>
          </div>
        )}
      </CardHeader>
      <CardContent className="px-3">
        <div className="space-y-2 mb-3">
          <div className="flex items-center gap-2 min-w-0">
            <p className="font-mono text-xs text-foreground truncate min-w-0">{current.case_id}</p>
            <Badge variant={confidenceVariant(current.confidence)} size="sm" className="shrink-0">
              {current.confidence}%
            </Badge>
          </div>
          {current.signals && current.signals.length > 0 && (
            <div className="flex flex-wrap gap-1">
              {current.signals.map((s) => (
                <Badge key={s.label} variant="outline" className="text-[11px]">
                  {s.label}
                  {s.score > 0 ? ` +${s.score}` : ''}
                </Badge>
              ))}
            </div>
          )}
          <p className="text-muted-foreground text-xs leading-relaxed">{current.confidence_summary}</p>
        </div>

        <div className="flex flex-wrap gap-2">
          <Button
            size="sm"
            onClick={() => onMatch(current.case_id)}
            disabled={isMatching}
            className="shrink-0"
            aria-label={`Match to case ${current.case_id}`}
          >
            {isMatching ? (
              <>
                <Loader2 className="size-3.5 animate-spin" aria-hidden />
                Matching…
              </>
            ) : (
              'Match to this case'
            )}
          </Button>
          <Button variant="ghost" size="sm" onClick={onDismiss} disabled={isMatching} aria-label="Dismiss suggestion">
            Dismiss
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
